import React, { useEffect, useMemo } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import color from "../../Constants/colors";

interface Props {
  svg: string;
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Best-effort intrinsic size (for crisp PNG rasterization) from width/height or
// the viewBox; falls back to a sane default.
const getSvgSize = (svg: string): { width: number; height: number } => {
  const w = svg.match(/<svg[^>]*\bwidth="([\d.]+)/i);
  const h = svg.match(/<svg[^>]*\bheight="([\d.]+)/i);
  if (w && h) {
    return { width: parseFloat(w[1]), height: parseFloat(h[1]) };
  }
  const vb = svg.match(
    /viewBox="\s*[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+([\d.]+)/i,
  );
  if (vb) {
    return { width: parseFloat(vb[1]), height: parseFloat(vb[2]) };
  }
  return { width: 800, height: 600 };
};

/**
 * Renders an agent-generated SVG diagram as an image with SVG/PNG download.
 * The SVG is loaded via an <img> blob URL on purpose: browsers disable scripting
 * for SVG loaded as an image, so even an unsanitized SVG cannot execute JS here.
 */
const DiagramBlock: React.FC<Props> = ({ svg }) => {
  const blobUrl = useMemo(
    () => URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" })),
    [svg],
  );
  useEffect(() => () => URL.revokeObjectURL(blobUrl), [blobUrl]);

  const downloadSvg = () =>
    triggerDownload(
      new Blob([svg], { type: "image/svg+xml" }),
      "diagram.svg",
    );

  const downloadPng = () => {
    const { width, height } = getSvgSize(svg);
    const srcUrl = URL.createObjectURL(
      new Blob([svg], { type: "image/svg+xml" }),
    );
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) triggerDownload(blob, "diagram.png");
        }, "image/png");
      }
      URL.revokeObjectURL(srcUrl);
    };
    img.onerror = () => URL.revokeObjectURL(srcUrl);
    img.src = srcUrl;
  };

  return (
    <Box
      sx={{
        my: 1,
        border: `1px solid ${color.gray200}`,
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: color.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 1.5,
          bgcolor: color.gray50,
        }}
      >
        <Box
          component="img"
          src={blobUrl}
          alt="Diagram"
          sx={{ maxWidth: "100%", height: "auto" }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderTop: `1px solid ${color.gray200}`,
        }}
      >
        <Tooltip title="Download SVG">
          <IconButton
            size="small"
            onClick={downloadSvg}
            sx={{ color: color.gray600 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download PNG">
          <IconButton
            size="small"
            onClick={downloadPng}
            sx={{ color: color.gray600 }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default React.memo(DiagramBlock);
